import { get } from 'svelte/store';
import { postReq } from '../services/http';
import { get_local_settings, get_local_status, get_local_device_info } from '../services/local_requests';

import { auth_locked_devices, device_auths } from 'src/stores/device-runtime/local-auth';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { syncAuthLockedFromAuthEnabled } from './auth_locked_helpers';
import { pending_connection_devices } from 'src/stores/device-runtime/firmware-ui';
import { get_local_ip, real_id, isXMODDevice, processXMODStatus } from './interface_helpers';
import { refresh_verified_local_ip } from './local_ip_probe_helpers';
import { requestEditDevice, requestEditSharedDevice } from 'src/components/Lists/list-helpers';
import { syncAppNamesFromDeviceSettings } from './device_name_sync_helpers';
import type { Device } from '../types/Device';

//#region get Device Status,Settings
export async function getSettings(device: Device) {
	const local_ip = get_local_ip(device.id);
	const id = real_id(device.id);

	if (local_ip) {
		const res = await get_local_settings(device, local_ip);

		if (res.isok) {
			const deviceInfoRes = await get_local_device_info(device, local_ip);
			const settings = deviceInfoRes?.isok ? { ...res, DeviceInfo: deviceInfoRes } : res;
			devices_settings.update_devices_settings(id, settings);
			void syncAppNamesFromDeviceSettings(id, settings);

			if (deviceInfoRes?.isok) {
				const authEn = (deviceInfoRes as any)?.auth_en;
				if (authEn === false) {
					syncAuthLockedFromAuthEnabled(id, false);
				}
				if (get(device_auths)[id]) {
					auth_locked_devices.remove(id);
				}
			} else if (
				!deviceInfoRes?.isok &&
				deviceInfoRes?.status === 401 &&
				!(get(device_auths)[id] != null)
			) {
				auth_locked_devices.add(id);
			}

			return res;
		} else {
			void refresh_verified_local_ip(device.id, { debounced: true });
			console.error('Failed to get settings - locally');
			return res;
		}
	} else {
		const res = await postReq(`/device/settings?id=${id}`, {
			id: id,
		});

		if (res?.isok) {
			if (res.data.device_settings.hasOwnProperty('online')) {
				if (device?.cloud_online !== res.data.device_settings.online) {
					device.cloud_online = res.data.device_settings.online;
					if (device?.shared) {
						await requestEditSharedDevice(device);
					} else {
						await requestEditDevice(device);
					}
				}
			}

			devices_settings.update_devices_settings(id, res.data.device_settings);
			void syncAppNamesFromDeviceSettings(id, res.data.device_settings);

			const cloudAuthEn = res.data.device_settings?.DeviceInfo?.auth_en;
			if (typeof cloudAuthEn === 'boolean') {
				syncAuthLockedFromAuthEnabled(id, cloudAuthEn);
			}

			return res;
		} else {
			if (res?.errors?.device_not_found) {
				console.log(res?.errors?.device_not_found);
			}
			console.error('Failed to get settings - cloud');
			return res;
		}
	}
}

export async function getStatus(device: Device) {
	const local_ip = get_local_ip(device.id);
	const id = real_id(device.id);

	if (local_ip) {
		const res = await get_local_status(device, local_ip);

		if (res.isok) {
			pending_connection_devices.remove(id);

			let status = res;
			status.online = true;
			// Assign status._updated a timestamp in UTC
			let now = +(Date.now() / 1000).toFixed(0);
			status._updated = now;

			if (isXMODDevice(device.type)) {
				status = await processXMODStatus(device, status);
			}

			devices_status.update_device_status({ id: id, msg: status });

			const deviceInfoRes = await get_local_device_info(device, local_ip);
			if (deviceInfoRes?.isok) {
				devices_settings.update_devices_settings(id, {
					...(devices_settings.getByRealId(id) || {}),
					DeviceInfo: deviceInfoRes,
				});
				const authEn = (deviceInfoRes as any)?.auth_en;
				if (authEn === false) {
					syncAuthLockedFromAuthEnabled(id, false);
				}
				if (get(device_auths)[id]) {
					auth_locked_devices.remove(id);
				}
			} else if (
				!deviceInfoRes?.isok &&
				deviceInfoRes?.status === 401 &&
				!(get(device_auths)[id] != null)
			) {
				auth_locked_devices.add(id);
			}

			return status;
		} else {
			void refresh_verified_local_ip(device.id, { debounced: true });
			console.error('Failed to get status - locally');
			return res;
		}
	} else {
		const res = await postReq(`/device/status?id=${id}`, {
			id: id,
		});

		if (res?.isok) {
			pending_connection_devices.remove(id);
			let status = res.data.device_status;
			status.online = res.data?.online;
			if (status?._updated) {
				// Convert _updated to an UTC timestamp
				let updated = status._updated.replace(' ', 'T') + 'Z';
				status._updated = +(new Date(updated).getTime() / 1000).toFixed(0);
			}

			if (isXMODDevice(device.type)) {
				status = await processXMODStatus(device, status);
			}

			return devices_status.update_device_status({
				id: id,
				msg: status,
			});
		} else {
			if (res?.errors?.device_not_found) {
				console.log(res?.errors?.device_not_found);
			}
			console.error('Failed to get status - cloud');
			return res;
		}
	}
}

//#endregion get Device Status,Settings
